import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Import the SVG files
import HeroImage from "../assets/photoGroup1.webp";
import Logo from "../assets/logo.svg";
import LogoTransparent from "../assets/logoTransparent.svg";
import ProfessorsImage from "../assets/photoGroup2.webp";
import RecommendationsImage from "../assets/photoGroup3.webp";
import SearchImage from "../assets/photoGroup4.webp";
import { ReactComponent as DotGroup1 } from "../assets/dotGroup1.svg";
import { ReactComponent as DotGroup2 } from "../assets/dotGroup2.svg";
import HeroFindMatches from "./HeroFindMatches";
import FeedbackModal from "./FeedbackModal";
import { useGuestPreview } from "../context/GuestPreviewContext";

const MediumHome = () => {
    const navigate = useNavigate();
    const { isGuestPreview } = useGuestPreview();
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

    const goToSignUp = () => {
        navigate('/register'); // Navigate to the signup page
    };

    const goToLoginOrApp = () => {
        if (isGuestPreview) {
            navigate('/login', { state: { fromGuestPreview: true } });
            return;
        }
        if (localStorage.getItem('authToken')) {
            navigate('/recommendations');
        } else {
            navigate('/login');
        }
    };

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div>
<FeedbackModal
    open={isFeedbackOpen}
    onClose={() => setIsFeedbackOpen(false)}
    requireEmailCaptcha
/>
            {/* Navbar */}
            <nav className="bg-white border-b-[0.8px] border-[#202531] h-20 z-50 relative">
                <div className="max-w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-full">
                        {/* Left Section: Logo */}
                        <Link to="/" className="flex items-center py-4" title="Home">
                            <img src={Logo} alt="Grad Connect" className="h-14" />
                        </Link>

                        {/* Right Section: Links and Buttons */}
                        <div className="flex items-center space-x-6 text-lg">
                            <button
                                onClick={() => scrollToSection('about')}
                                className="text-black hover:text-[#0196BF] transition font-medium"
                            >
                                About
                            </button>
                            <button
                                onClick={() => scrollToSection('features')}
                                className="text-black hover:text-[#0196BF] transition font-medium"
                            >
                                Features
                            </button>
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={goToSignUp}
                                    className="border border-[#0196BF] text-black px-5 py-3 rounded-full hover:bg-[#E0F7FF] transition w-28 font-medium"
                                >
                                    Sign Up
                                </button>
                                <button
                                    type="button"
                                    onClick={goToLoginOrApp}
                                    className="border border-[#0196BF] bg-[#0196BF] text-white px-5 py-3 rounded-full hover:bg-[#017BA3] transition w-28 font-medium"
                                >
                                    Log In
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section
                id="about"
                className="relative flex justify-between items-center px-6 py-12 bg-gray-50"
                style={{ flexWrap: 'nowrap' }}
            >
                {/* Background Circle */}
                <div
                    className="absolute top-[-15vw] left-[-15vw] w-[70vw] h-[70vw] bg-[#202531] opacity-10 rounded-full z-0"
                    aria-hidden="true"
                ></div>

                <div
                    className="absolute top-[300px] left-[80vw] w-[30vw] h-[30vw] bg-[#202531] opacity-10 rounded-full z-0"
                    aria-hidden="true"
                ></div>

                {/* Text Section */}
                <div
                    className="flex-1 text-left relative z-10 max-w-[50%] px-4"
                    style={{ flexShrink: 1 }}
                >
                    {/* First Text (p) */}
                    <p
                        className="text-2xl text-gray-600 leading-relaxed max-w-[360px]"
                    >
                        Success in grad school begins with the right advisor.
                    </p>

                    {/* Second Text (h1) */}
                    <motion.h1
                        className="text-3xl font-bold text-gray-900 mt-4 leading-tight max-w-[420px]"

                        initial={{ opacity: 0 }}

                        animate={{ opacity: 1 }}

                        transition={{ duration: 1.1, delay: 0.35, ease: "easeOut" }}

                    >

                        Grad Connect helps you find your perfect match.

                    </motion.h1>

                    <HeroFindMatches layout="medium" />
                </div>

                {/* Image Section */}
                <div
                    className="flex-1 flex justify-center items-center max-w-[50%] px-4"
                    style={{ flexShrink: 1 }}
                >
                    <img src={HeroImage}
                        className="w-[450px] h-auto max-w-full transform translate-x-[4%] translate-y-[-4%] transition-all duration-300"
                    />
                </div>
            </section>
            <div className="border-t-[0.8px] border-[#202531] w-screen"></div> {/* Original border thickness */}

            {/* Features Section */}
            <section
                id="features"
                className="relative flex justify-between items-center px-6 py-12 bg-gray-50"
                style={{ flexWrap: 'nowrap' }}
            >
                {/* Text Section */}
                <div
                    className="flex-1 text-left relative z-10 max-w-[50%] px-4"
                    style={{ flexShrink: 1 }}
                >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">
                        Every Professor in One Place
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        No more searching school directories. Grad Connect has all the information you need to evaluate advisors from every university in the US.
                    </p>
                    <div className="space-y-6">
                        <div
                            className="flex items-center pl-6"
                        >
                            <span className="text-[#FDA800] text-2xl mr-3">✔</span>
                            <p className="text-lg text-gray-700">Clear Research Summaries</p>
                        </div>
                        <div
                            className="flex items-center pl-6"
                        >
                            <span className="text-[#E50C33] text-2xl mr-3">✔</span>
                            <p className="text-lg text-gray-700">
                                Links to Personal Website and Google Scholar Profile
                            </p>
                        </div>
                        <div
                            className="flex items-center pl-6"
                        >
                            <span className="text-[#0196BF] text-2xl mr-3">✔</span>
                            <p className="text-lg text-gray-700">Contact Information</p>
                        </div>
                    </div>
                </div>

                {/* Image Section */}
                <div
                    className="flex-1 flex justify-center items-center max-w-[50%] px-4"
                    style={{ flexShrink: 1 }}
                >
                    <img src={ProfessorsImage}
                        className="w-[450px] h-auto max-w-full transform translate-x-[4%] translate-y-[-4%] transition-all duration-300"
                    />
                </div>
            </section>


{/* Intelligent Recommendations */}
<section
    className="relative flex justify-between items-center px-6 py-12 bg-gray-50"
    style={{ flexWrap: 'nowrap' }}
>

    {/* Image Section */}
    <div
        className="flex-1 flex justify-center items-center max-w-[45%] px-4"
        style={{ flexShrink: 1 }}
    >
        <img src={RecommendationsImage}
            className="w-[400px] h-auto max-w-full transform translate-x-[4%] translate-y-[-4%] transition-all duration-300"
        />
    </div>

    {/* Text Section */}
    <div
        className="flex-1 text-left relative z-10 max-w-[50%] -mt-16 px-4"
        style={{ flexShrink: 1 }}
    >
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Intelligent Recommendations
        </h2>
        <p className="text-lg text-gray-700 mb-8 max-w-[420px]">
            Grad Connect helps you discover professors whose research aligns with your interests—even ones from schools you’ve never considered.
        </p>
        <p className="text-lg text-gray-700 max-w-[420px]">
            The more you interact with professor profiles, the better the recommendations become.
        </p>
    </div>
</section>


{/* Organize Your Search */}
<section
    className="relative flex justify-between items-center px-6 py-12 bg-gray-50"
    style={{ flexWrap: 'nowrap' }}
>

    {/* Text Section */}
    <div
        className="flex-1 text-left relative z-10 max-w-[50%] -mt-12 px-4"
        style={{ flexShrink: 1 }}
    >
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Organize Your Search
        </h2>
        <p className="text-lg text-gray-700 mb-8 max-w-[420px]">
            Grad Connect lets you search for professors by name, school, or research topic. You can filter results by school or department, and easily bookmark professors you're interested in.
        </p>
        <p className="text-lg text-gray-700 max-w-[420px]">
            All their information in one place, making it simple to reach out when you're ready.
        </p>
    </div>

    {/* Image Section */}
    <div
        className="flex-1 flex justify-center items-center max-w-[50%] px-4"
        style={{ flexShrink: 1 }}
    >
        <img src={SearchImage}
            className="w-[450px] h-auto max-w-full transform translate-x-[4%] translate-y-[-4%] transition-all duration-300"
        />
    </div>
</section>




{/* New "The Best Part" Section */}
            <div className="border-t-[0.8px] border-[#202531] w-screen"></div>

<section className="relative flex flex-col items-center py-24 bg-gray-50">
    {/* Decorative Dot Backgrounds */}
    <div className="absolute top-[10%] left-[10%] w-[90px] h-[90px] z-0 transform rotate-[95deg]">
        <DotGroup1 className="w-full h-full" />
    </div>
    <div className="absolute top-[60%] left-[80%] w-[90px] h-[90px] z-0 transform rotate-[85deg]">
        <DotGroup2 className="w-full h-full" />
    </div>

    {/* Text */}
    <h2
        className="text-3xl font-bold text-gray-900 mb-3 relative z-10"
    >
        The Best Part?
    </h2>

    <h3
        className="text-4xl font-extrabold text-[#E50C33] mb-6 relative z-10"
    >
        Grad Connect is free for students.
    </h3>

    {/* Button */}
    <button
        onClick={goToSignUp}
        className="bg-[#0196BF] text-white font-bold text-2xl px-6 py-3 rounded-full hover:bg-[#017BA3] transition relative z-10"
    >
        Get Started Now
    </button>
</section>




{/* Footer */}
<div className="border-t-[0.8px] border-[#202531] w-screen"></div> {/* Original border thickness */}
<footer className="bg-[#E0E0E2] py-4">
    <div className="mx-auto text-center px-8 max-w-[1200px]">
        {/* Logo */}
        <div className="mb-4">
            <img src={LogoTransparent} alt="GradConnect Logo" className="h-12 mx-auto" />
        </div>

        {/* Links */}
        <div className="flex justify-center space-x-2 mb-4">
            <a href="/register" className="text-gray-700 hover:text-[#0196BF] transition">
                Sign Up
            </a>
            <span className="text-gray-700">•</span> {/* Dot separator */}
            <a href="/login" className="text-gray-700 hover:text-[#0196BF] transition">
                Sign In
            </a>
            <span className="text-gray-700">•</span> {/* Dot separator */}
            <button
                type="button"
                onClick={() => setIsFeedbackOpen(true)}
                className="text-gray-700 hover:text-[#0196BF] transition"
            >
                Give feedback
            </button>
        </div>

        {/* Copyright */}
        <p className="text-sm text-gray-600 mt-4">
            &copy; 2026 GradConnect LLC
        </p>
    </div>
</footer>




        </div>
    );
};

export default MediumHome;
